
import React, {
  lazy, Suspense, useCallback, useEffect, useState,
} from 'react';
import { Provider, useDispatch } from 'react-redux';
import { BrowserRouter, Switch } from 'react-router-dom';
import {
  CssBaseline,
  ThemeProvider,
  StyledEngineProvider,
  createTheme,
  adaptV4Theme,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import {
  logoutAction, resumeLastSessionAction,
} from '@ivymobi/orbit-core/business/user';
import configureStore from '@ivymobi/orbit-core/business/configureStore';
import * as api from './api';
import themeConfig from './services/theme';
import Loader from './components/loaders';
import { InnerUserContext, ThemeContext, UserContext } from './orbit-context';
import { queryParse } from '@ivymobi/orbit-core';
import { I18nextProvider } from 'react-i18next';
import i18n from './services/i18n';
import PreProcess from './components/preProcess';

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

const ActiveContainer = lazy(
  () => import('./mycontain' /* webpackChunkName: "user" */)
);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      position: 'relative',
      zIndex: 1,
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      height: '100%',
      // minWidth: 'fit-content',
      width: '100%',
      backgroundColor: theme.palette.background.default,
      color: theme.palette.getContrastText(
        theme.palette.background.default
      ),
    },
  })
);

const Main = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const _innerUser = queryParse(window.location.search);
  const [innerUser, setInnerUser] = useState(_innerUser??{});
  const [user, setUser] = useState(null);

  useEffect(() => {
    dispatch(resumeLastSessionAction()).then(
      (userData) => {
        userData.logout = logout;
        setUser(userData);
      },
      () => setUser({}));
  }, []);

  const doLogin = (userData) => {
    userData.logout = logout;
    setUser(userData);
  };

  const logout = useCallback(() => {
    dispatch(logoutAction())
    .then(() => {
      setUser({});
    });
  }, [dispatch, setUser]);

  return (
    <div className={classes.root}>
      <UserContext.Provider value={user}>
        <InnerUserContext.Provider value={innerUser}>
          <Switch>
            <ActiveContainer  doLogin={doLogin} />
          </Switch>
        </InnerUserContext.Provider>
      </UserContext.Provider>
    </div>
  );
};

export default function OrbitApp() {
  const store = configureStore(api, {});
  // const [mainColor, setMainColor] = useState('#005ae7');
  themeConfig.palette.primary.main = '#005ae7';
  const theme = createTheme(themeConfig);
  // const [theme, setTheme] = useState(createTheme(adaptV4Theme(themeConfig)));
  const handleChangeTheme = (color) => {
    // if (color === mainColor) return;
    theme.palette.primary.main = color;
  };

  return (
    <BrowserRouter>
      <ThemeContext.Provider value={handleChangeTheme}>
        <CssBaseline />
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <Provider store={store}>
              <PreProcess>
                <I18nextProvider i18n={i18n}>
                  <Suspense fallback={<Loader />} >
                    <Main />
                  </Suspense>
                </I18nextProvider>
              </PreProcess>
            </Provider>
          </ThemeProvider>
        </StyledEngineProvider>
      </ThemeContext.Provider>
    </BrowserRouter>
  );
}
